import { forwardRef, memo } from 'react';
import { cn, List } from '@divlab/divanui';

import { useRequest } from '@Contexts/Request';
import useTranslation from '@Queries/useTranslation';
import useCountryBasedData from '@Hooks/useCountryBasedData';
import Img from '@UI/Img';
import delivery from './icons/delivery.svg';
import quality from './icons/quality.svg';
import payment from './icons/payment.svg';
import exchange from './icons/exchange.svg';
import styles from './ComfortBuy.module.css';

import type { HTMLAttributes } from 'react';
import type { CountryBasedConfig } from '@Hooks/useCountryBasedData';

export interface Advantage {
  img: { src: string; alt?: string };
  description: string;
}

export interface ComfortBuyProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
}

const ComfortBuy = forwardRef<HTMLDivElement, ComfortBuyProps>(function ComfortBuy(props, ref) {
  const { className, ...restProps } = props;
  const { country } = useRequest();
  const { t } = useTranslation();

  const advantagesData: CountryBasedConfig<Advantage[]> = {
    BLR: [
      {
        img: { src: quality, alt: 'Гарантия' },
        description: t('comfort-buy.advantages.quality'),
      },
      {
        img: { src: payment, alt: 'Способы оплаты' },
        description: t('comfort-buy.advantages.payment'),
      },
      {
        img: { src: delivery, alt: 'Доставка' },
        description: t('comfort-buy.advantages.delivery'),
      },
    ],
    default: [
      { img: { src: quality }, description: t('comfort-buy.advantages.quality') },
      {
        img: { src: payment },
        description: t('comfort-buy.advantages.payment'),
      },
      {
        img: { src: exchange },
        description: t('comfort-buy.advantages.exchange'),
      },
      {
        img: { src: delivery },
        description: t('comfort-buy.advantages.delivery'),
      },
    ],
  };
  const staticData = {
    title: t('comfort-buy.title'),
    advantages: useCountryBasedData(advantagesData),
    note: t('comfort-buy.note'),
  };

  return (
    <div {...restProps} ref={ref} className={cn(styles.comfortBuy, className)}>
      <div className={styles.heading}>{staticData.title}</div>
      <List
        className={styles.advantagesList}
        items={staticData.advantages}
        renderChild={(advantage: Advantage) => (
          <div className={styles.advantagesItem}>
            <Img
              className={styles.advantagesPic}
              src={advantage.img.src}
              alt={advantage.img.alt}
              cnImage={styles.cnImage}
            />
            <div
              className={styles.description}
              dangerouslySetInnerHTML={{ __html: advantage.description }}
            />
          </div>
        )}
      />
      {country === 'BLR' && <div className={styles.note}>{staticData.note}</div>}
    </div>
  );
});

export default memo(ComfortBuy);
